import blueimpLoadImage, { type LoadImageOptions as BlueimpLoadImageOptions } from 'blueimp-load-image';

export interface ImageLoadOptions {
	crossOrigin?: '' | 'anonymous' | 'use-credentials' | null;
}

/**
 * Chooses the browser credential mode used by Creator's established image
 * decoding pipeline.
 *
 * @param source Image URL passed to blueimp.
 * @param crossOrigin Explicit credential mode supplied by the caller.
 * @returns Credential mode assigned to blueimp, or `null` for browser defaults.
 */
function resolveCrossOrigin(
	source: string,
	crossOrigin: ImageLoadOptions['crossOrigin'],
): string | null {
	if (typeof crossOrigin !== 'undefined') {
		return crossOrigin;
	}

	return /^https?:\/\//i.test(source)
		? 'Anonymous'
		: null;
}

/**
 * Checks the same decoded-image invariants used by Creator's image service.
 *
 * @param image Image returned by blueimp.
 * @returns Whether the image can safely be drawn to a canvas.
 */
function validateImage(image: HTMLImageElement): boolean {
	image.onload = null;

	if (image.naturalHeight + image.naturalWidth === 0) {
		return false;
	}

	if (
		typeof image.complete !== 'undefined'
		&& image.complete === false
	) {
		return false;
	}

	image.onerror = null;

	return true;
}

/**
 * Decodes a browser image through the same blueimp path used by Creator's
 * legacy EditorDraw background loader, while keeping application counters and
 * telemetry outside the shared tools package.
 * Failure messages omit the source because URLs and inline images can contain
 * credentials or customer content that host error reporters must not collect.
 *
 * @param source Non-empty URL, data URL, or blob URL to decode.
 * @param options Optional browser credential behavior.
 * @returns A promise resolved with the validated image element.
 */
export function loadImage(
	source: string,
	options: ImageLoadOptions = {},
): Promise<HTMLImageElement> {
	if (!source) {
		return Promise.reject(new Error('Could not load an empty image source'));
	}

	const blueimpOptions: BlueimpLoadImageOptions = {
		canvas: false as boolean,
		meta: false,
		orientation: false,
	};
	const crossOrigin = resolveCrossOrigin(
		source,
		options.crossOrigin,
	);

	if (crossOrigin) {
		blueimpOptions.crossOrigin = crossOrigin;
	}

	return new Promise((resolve, reject) => {
		blueimpLoadImage(
			source,
			(image) => {
				if (
					typeof HTMLImageElement !== 'undefined'
					&& image instanceof HTMLImageElement
					&& validateImage(image)
				) {
					resolve(image);
					return;
				}

				reject(new Error('Could not load image'));
			},
			blueimpOptions,
		);
	});
}

export default loadImage;
